import { createBrowserRouter, Outlet } from 'react-router-dom'

import { IndicationsProvider } from '@/contexts/indications-context'
import { PropertyFormProvider } from '@/contexts/property-context'
import { SidebarProvider } from '@/contexts/sidebar-context'
import Layout from '@/layout'
import { About } from '@/pages/about/about'
import { ActivateEmail } from '@/pages/auth/activate-email'
import { EmailResetPassword } from '@/pages/auth/email-reset-password'
import { Login } from '@/pages/auth/login'
import { LayoutBackOffice } from '@/pages/backoffice/layout'
import { LayoutAdmin } from '@/pages/backoffice/layout-admin'
import { LayoutMonitoringBackOffice } from '@/pages/backoffice/simulation/layout'
import { BeGuardian } from '@/pages/be-guardian/be-guardian'
import { Contact } from '@/pages/contact'
import { Guardians } from '@/pages/guardians/guardians'
import { Home } from '@/pages/home/home'
import { MorePublications } from '@/pages/publications/more-publications'
import { Publications } from '@/pages/publications/publications'
import { RegisterPreserver } from '@/pages/register/preserver'
import { Register } from '@/pages/register/register'
import { RegisterSponsor } from '@/pages/register/sponsor'
import { Services } from '@/pages/services/services'
import { LayoutCompletePreserver } from '@/pages/supervisor/layout-complete-preserver'
import { Technologies } from '@/pages/technologies/technologies'
import { ProtectedRoutes } from '@/services/protected-routes'

import {
  AddAndEditCertificate,
  AddAndEditPerimeterCoordinates,
  AddAndEditProperty,
  AddAndEditSponsorship,
  AgentContractPreview,
  BankDetails,
  Carbon,
  CheckScore,
  CommissionsList,
  ConfirmSponsor,
  ContactMessage,
  ContactSponsorship,
  CreateAgentContractById,
  CreateAndEditAgentIndications,
  CreateAndEditBanner,
  CreateAndEditPublication,
  CreateCompletePreserver,
  CreateCompleteSupervisor,
  CreatingAgentByAdmin,
  Damping,
  Dashboard,
  DesignSystem,
  EditProfile,
  FinancialDataSponsorship,
  Iconography,
  InstallmentsList,
  IntegrationWorkerAndBlockchain,
  LGPDContacts,
  ListAgents,
  ListBanners,
  ListDocuments,
  ListGuardians,
  ListLgpdUsers,
  ListLogs,
  ListPayments,
  ListPreservers,
  ListProperties,
  ListPublications,
  ListScores,
  ListSignatories,
  ListSignatoriesAssociation,
  ListSignatoryRoles,
  ListSponsors,
  ListSponsorships,
  ListSupervisors,
  ListTeam,
  ListTestimonials,
  ListUsers,
  ListUsersTypes,
  MessageSponsorship,
  Monitoring,
  NotAllowed,
  NotFound,
  Ping,
  Preservation,
  PreservationContract,
  ProcessSimulation,
  Property,
  PublicationDetail,
  Regeneration,
  RegisterAgent,
  RegisterUser,
  ResetPassword,
  ViewAgentContract,
  ViewContract,
  ViewPreviewContract,
  ViewProperty,
  WebsiteConfig,
} from './lazy-page'
import { LazyRoute } from './lazy-route'

export const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      {
        path: 'not-allowed',
        element: (
          <LazyRoute>
            <NotAllowed />
          </LazyRoute>
        ),
      },
      { path: '/', element: <Home /> },
      {
        path: '/design-system',
        element: (
          <LazyRoute>
            <DesignSystem />
          </LazyRoute>
        ),
      },
      {
        path: '/icons',
        element: (
          <LazyRoute>
            <Iconography />
          </LazyRoute>
        ),
      },
      { path: '/publications', element: <Publications /> },
      {
        path: '/more-publications/:publicationType',
        element: <MorePublications />,
      },
      {
        path: '/preservation',
        element: (
          <LazyRoute>
            <Preservation />
          </LazyRoute>
        ),
      },
      {
        path: '/monitoring',
        element: (
          <LazyRoute>
            <Monitoring />
          </LazyRoute>
        ),
      },
      {
        path: '/regeneration',
        element: (
          <LazyRoute>
            <Regeneration />
          </LazyRoute>
        ),
      },
      {
        path: '/damping',
        element: (
          <LazyRoute>
            <Damping />
          </LazyRoute>
        ),
      },
      {
        path: '/publication/:publicationId',
        element: (
          <LazyRoute>
            <PublicationDetail />
          </LazyRoute>
        ),
      },
      { path: '/about', element: <About /> },
      { path: '/services', element: <Services /> },
      { path: '/login', element: <Login /> },
      {
        path: '/reset-password/:idToken',
        element: (
          <LazyRoute>
            <ResetPassword />
          </LazyRoute>
        ),
      },
      { path: '/reset-password/email', element: <EmailResetPassword /> },
      { path: '/confirm-email/:token', element: <ActivateEmail /> },
      {
        path: '/carbon',
        element: (
          <LazyRoute>
            <Carbon />
          </LazyRoute>
        ),
      },
      {
        path: '/guardians',
        element: <Guardians />,
      },
      { path: '/be-guardian', element: <BeGuardian /> },
      { path: '/technologies', element: <Technologies /> },
      { path: '/contact', element: <Contact /> },
      { path: '/register', element: <Register /> },
      { path: '/register/preserver', element: <RegisterPreserver /> },
      { path: '/register/sponsor', element: <RegisterSponsor /> },
      {
        path: '/register/agent',
        element: (
          <LazyRoute>
            <RegisterAgent />
          </LazyRoute>
        ),
      },
      {
        path: '/ping',
        element: (
          <LazyRoute>
            <Ping />
          </LazyRoute>
        ),
      },
      {
        path: '/contact-message',
        element: (
          <LazyRoute>
            <ContactMessage />
          </LazyRoute>
        ),
      },
      {
        path: '/property/:idProperty',
        element: (
          <LazyRoute>
            <Property />
          </LazyRoute>
        ),
      },
      {
        path: '/lgpd-contacts',
        element: (
          <LazyRoute>
            <LGPDContacts />
          </LazyRoute>
        ),
      },

      {
        element: <ProtectedRoutes />,
        children: [
          {
            path: '/dashboard',
            element: (
              <LazyRoute>
                <Dashboard />
              </LazyRoute>
            ),
          },
          {
            path: '/edit-profile/:userId/:mode?',
            element: (
              <LazyRoute>
                <EditProfile />
              </LazyRoute>
            ),
          },
          {
            path: '/bank-details/:userId',
            element: (
              <LazyRoute>
                <BankDetails />
              </LazyRoute>
            ),
          },
          {
            path: '/dashboard/register-user',
            element: (
              <LazyRoute>
                <RegisterUser />
              </LazyRoute>
            ),
          },
          {
            path: '/view-property/:idProperty',
            element: (
              <LazyRoute>
                <ViewProperty />
              </LazyRoute>
            ),
          },
          {
            path: '/view-contract/:idContract',
            element: (
              <LazyRoute>
                <ViewContract />
              </LazyRoute>
            ),
          },
          {
            path: '/agent/commissions/:indicationId',
            element: (
              <LazyRoute>
                <CommissionsList />
              </LazyRoute>
            ),
          },
          {
            path: '/agent/commissions/:indicationId/installments/:commissionId',
            element: (
              <LazyRoute>
                <InstallmentsList />
              </LazyRoute>
            ),
          },
          {
            path: '/',
            element: (
              <PropertyFormProvider>
                <Outlet />
              </PropertyFormProvider>
            ),
            children: [
              {
                path: '/add-property',
                element: (
                  <LazyRoute>
                    <AddAndEditProperty />
                  </LazyRoute>
                ),
              },
              {
                path: '/add-property/:userId',
                element: (
                  <LazyRoute>
                    <AddAndEditProperty />
                  </LazyRoute>
                ),
              },
              {
                path: '/add-property/perimeter-coordinates',
                element: (
                  <LazyRoute>
                    <AddAndEditPerimeterCoordinates />
                  </LazyRoute>
                ),
              },
              {
                path: '/add-property/perimeter-coordinates/:userId',
                element: (
                  <LazyRoute>
                    <AddAndEditPerimeterCoordinates />
                  </LazyRoute>
                ),
              },
              {
                path: '/edit-property/perimeter-coordinates/:idProperty',
                element: (
                  <LazyRoute>
                    <AddAndEditPerimeterCoordinates />
                  </LazyRoute>
                ),
              },
              {
                path: '/edit-property/:idProperty',
                element: (
                  <LazyRoute>
                    <AddAndEditProperty />
                  </LazyRoute>
                ),
              },
            ],
          },
          {
            path: '/add-property/contract/:idProperty',
            element: (
              <LazyRoute>
                <PreservationContract />
              </LazyRoute>
            ),
          },
          {
            path: '/edit-property/contract/:idProperty',
            element: (
              <LazyRoute>
                <PreservationContract />
              </LazyRoute>
            ),
          },
          {
            path: '/reset-password',
            element: (
              <LazyRoute>
                <ResetPassword />
              </LazyRoute>
            ),
          },
          {
            path: '/contact-sponsorship/:idProperty',
            element: (
              <LazyRoute>
                <ContactSponsorship />
              </LazyRoute>
            ),
          },
          {
            path: '/contact-sponsorship/message',
            element: (
              <LazyRoute>
                <MessageSponsorship />
              </LazyRoute>
            ),
          },
          {
            path: '/confirm-sponsor/:idProperty',
            element: (
              <LazyRoute>
                <ConfirmSponsor />
              </LazyRoute>
            ),
          },

          {
            path: '/contract/agent/:agentId/:contractId?',
            element: (
              <LazyRoute>
                <ViewAgentContract />
              </LazyRoute>
            ),
          },
          {
            element: <LayoutCompletePreserver />,
            children: [
              {
                path: '/create/complete/preserver',
                element: (
                  <LazyRoute>
                    <CreateCompletePreserver />
                  </LazyRoute>
                ),
              },
            ],
          },
          {
            element: <LayoutAdmin />,
            children: [
              {
                path: '/backoffice/create/complete/supervisor',
                element: (
                  <LazyRoute>
                    <CreateCompleteSupervisor />
                  </LazyRoute>
                ),
              },

              {
                path: '/backoffice/create/agent',

                element: (
                  <LazyRoute>
                    <CreatingAgentByAdmin />
                  </LazyRoute>
                ),
              },
              {
                path: '/backoffice/indications/agent/:agentId',
                element: (
                  <LazyRoute>
                    <IndicationsProvider>
                      <CreateAndEditAgentIndications />
                    </IndicationsProvider>
                  </LazyRoute>
                ),
              },
              {
                path: '/backoffice/indications/visualization/:indicationId',
                element: (
                  <LazyRoute>
                    <CommissionsList />
                  </LazyRoute>
                ),
              },
            ],
          },
        ],
      },

      {
        path: '/backoffice',
        element: (
          <SidebarProvider>
            <LayoutBackOffice />
          </SidebarProvider>
        ),
        children: [
          // TODO: Tirar comentário quando tiver a tela de log
          // { path: 'dashboard', element: <BackOfficeDashboard /> },

          {
            path: 'preservers',
            element: (
              <LazyRoute>
                <ListPreservers />
              </LazyRoute>
            ),
          },
          {
            path: 'sponsors',
            element: (
              <LazyRoute>
                <ListSponsors />
              </LazyRoute>
            ),
          },
          {
            path: 'config',
            element: (
              <LazyRoute>
                <WebsiteConfig />
              </LazyRoute>
            ),
          },
          {
            path: 'profile/association',
            element: (
              <LazyRoute>
                <ListUsersTypes />
              </LazyRoute>
            ),
          },
          {
            path: 'agents',
            element: (
              <LazyRoute>
                <ListAgents />
              </LazyRoute>
            ),
          },
          {
            path: 'supervisors',
            element: (
              <LazyRoute>
                <ListSupervisors />
              </LazyRoute>
            ),
          },
          {
            path: 'agent',
            children: [
              {
                path: 'contract/:agentId/:contractId?',
                element: (
                  <LazyRoute>
                    <CreateAgentContractById />
                  </LazyRoute>
                ),
              },
              {
                path: 'contract/preview/:contractId',
                element: (
                  <LazyRoute>
                    <AgentContractPreview />
                  </LazyRoute>
                ),
              },
            ],
          },
          {
            path: 'properties',
            element: (
              <LazyRoute>
                <ListProperties />
              </LazyRoute>
            ),
          },
          {
            path: 'sponsorships',
            element: (
              <LazyRoute>
                <ListSponsorships />
              </LazyRoute>
            ),
          },
          {
            path: 'sponsorships/documents/:contractId',
            element: (
              <LazyRoute>
                <ListDocuments />
              </LazyRoute>
            ),
          },
          {
            path: 'users',
            element: (
              <LazyRoute>
                <ListUsers />
              </LazyRoute>
            ),
          },
          {
            path: 'lgpd/users',
            element: (
              <LazyRoute>
                <ListLgpdUsers />
              </LazyRoute>
            ),
          },
          {
            path: 'publications',
            element: (
              <LazyRoute>
                <ListPublications />
              </LazyRoute>
            ),
          },
          {
            path: 'signatories',
            element: (
              <LazyRoute>
                <ListSignatories />
              </LazyRoute>
            ),
          },
          {
            path: 'signatory/associations',
            element: (
              <LazyRoute>
                <ListSignatoriesAssociation />
              </LazyRoute>
            ),
          },
          {
            path: 'signatory-roles',
            element: (
              <LazyRoute>
                <ListSignatoryRoles />
              </LazyRoute>
            ),
          },
          {
            path: 'guardians',
            element: (
              <LazyRoute>
                <ListGuardians />
              </LazyRoute>
            ),
          },
          {
            path: 'team',
            element: (
              <LazyRoute>
                <ListTeam />
              </LazyRoute>
            ),
          },
          {
            path: 'testimonials',
            element: (
              <LazyRoute>
                <ListTestimonials />
              </LazyRoute>
            ),
          },
          {
            path: 'banners',
            element: (
              <LazyRoute>
                <ListBanners />
              </LazyRoute>
            ),
          },
          {
            path: 'payments',
            element: (
              <LazyRoute>
                <ListPayments />
              </LazyRoute>
            ),
          },
          {
            path: 'scores',
            element: (
              <LazyRoute>
                <ListScores />
              </LazyRoute>
            ),
          },
          {
            path: 'banners/:idBanner',
            element: (
              <LazyRoute>
                <CreateAndEditBanner />
              </LazyRoute>
            ),
          },
          {
            path: 'publications/create',
            element: (
              <LazyRoute>
                <CreateAndEditPublication />
              </LazyRoute>
            ),
          },
          {
            path: 'publications/:publicationId',
            element: (
              <LazyRoute>
                <CreateAndEditPublication />
              </LazyRoute>
            ),
          },
          {
            path: 'log',
            element: (
              <LazyRoute>
                <ListLogs />
              </LazyRoute>
            ),
          },
          {
            path: 'add-sponsorship/:idProperty/:idContractSponsorship?',
            element: (
              <LazyRoute>
                <AddAndEditSponsorship />
              </LazyRoute>
            ),
          },
          {
            path: 'edit-sponsorship/:idProperty/:idContractSponsorship',
            element: (
              <LazyRoute>
                <AddAndEditSponsorship />
              </LazyRoute>
            ),
          },
          {
            path: 'preview-contract/:idProperty/:idContractSponsorship',
            element: (
              <LazyRoute>
                <ViewPreviewContract />
              </LazyRoute>
            ),
          },
          {
            path: 'financial-data-sponsorship/:idProperty/:idContractSponsorship',
            element: (
              <LazyRoute>
                <FinancialDataSponsorship />
              </LazyRoute>
            ),
          },
          {
            path: 'property/certificate/:idProperty',
            element: (
              <LazyRoute>
                <AddAndEditCertificate />
              </LazyRoute>
            ),
          },
          {
            path: 'integration/worker/blockchain',
            element: (
              <LazyRoute>
                <IntegrationWorkerAndBlockchain />
              </LazyRoute>
            ),
          },
          {
            path: 'check/score/:userId',
            element: (
              <LazyRoute>
                <CheckScore />
              </LazyRoute>
            ),
          },
        ],
      },
      {
        element: <LayoutMonitoringBackOffice />,
        children: [
          {
            path: '/backoffice/process/simulation',
            element: (
              <LazyRoute>
                <ProcessSimulation />
              </LazyRoute>
            ),
          },
        ],
      },
    ],
    errorElement: (
      <LazyRoute>
        <NotFound />
      </LazyRoute>
    ),
  },
])
